<template>
  <div class="c-block-management w-full h-full">
    <CBlockManagementToolbar
      ref="toolbarWrapper"
      :custom-filter-components="dataFilterComponents"
      :hide-create="isImpact"
      :hide-view="!launchDarkly.allow_canvases_tables_columns_settings"
      :is-loading-items="isLoading"
      :metadata-filter-values="filters"
      :search="search"
      :tableColumnsSettings="tableColumnsSettings"
      @create:block="emit('create')"
      @move="onColumnsMove"
      @restoreColumns="restoreColumns"
      @search="onTableChange('searchChanged', $event)"
      @select="onColumnsSelect"
      @update:filter-values="onTableChange('filterValuesChanged', $event)"
    />
    <CTable
      ref="tableRef"
      class="mt-3"
      :columns="launchDarkly.allow_canvases_tables_columns_settings ? columnsFromSettings : defaultColumns"
      :data="filteredData"
      :initialSorting="initialSorting"
      :loading="isLoadingTable"
      :page-size="pageSize"
      :page-sizes="DEFAULT_PAGE_SIZE_OPTIONS"
      :scroller-style="{ height: `calc(100vh - 320px - ${blockManagementToolbarHeight || 0}px)` }"
      @page-changed="onTableChange('pageChanged', $event)"
      @page-size-changed="onTableChange('pageSizeChanged', $event)"
      @sort-changed="onTableChange('sortChanged', $event)"
    />
    <slot />
  </div>
</template>

<script setup lang="tsx">
import { h, provide, ref, onMounted, computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useElementSize } from '@vueuse/core'
import { type ColumnDef, createColumnHelper, type PaginationState, type SortingState } from '@tanstack/table-core'
import { isEqual, omit } from 'lodash-es'

import { DEFAULT_PAGE_SIZE_OPTIONS } from '@lib/constants/tables.const'
import CTable from '@lib/components/CTable/CTable.vue'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import CTableTag from '@lib/components/CTableTag/CTableTag.vue'
import CBlockManagementToolbar from './CBlockManagementToolbar.vue'
import CBlockManagementActionsCell from './CBlockManagementActionsCell.vue'
import CCanvasDeleteDialogContent from '@lib/components/canvases/CCanvasDeleteDialogContent.vue'
import useConfirmation from '@lib/composables/useConfirmation'
import useThemeVars from '@lib/composables/useThemeVars'
import { fetchAll } from '@lib/util/network'
import type { PaginatedData } from '@lib/types/paginatedData'
import { CLIENT_TYPE } from '@lib/constants/cdp.const'
import { formatDateTime, dayjs } from '@lib/util/date'
import { type GetCanvasesParams } from '@lib/types/canvases'
import { isPitcherWkWebView } from '@lib/util/userAgents'
import { isModifierClick } from '@lib/util/navigation.util'
import type { CanvasRetrieve, CanvasCreateRequest, PatchedCanvasUpdateRequest, User } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import useToast from '@lib/composables/useToast'
import { useTableColumnsSettings } from '@lib/composables/tableColumnsSettings.use'
import usePagination from '@lib/composables/useCanvasTablesPagination'
import useSuggestedTags from '@lib/composables/suggestedTags.use'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { PitcherInfo } from '@lib/types/app'
import CSwitch from '@lib/components/CSwitch/CSwitch.vue'
import CThumbnail from '@lib/components/CThumbnail/CThumbnail.vue'
import { useCanvasStandardFilters } from '@lib/composables/useCanvasStandardFilters'

const props = defineProps<{
  storageKey?: string
  myUserId?: User['id']
  updater: (
    params: PatchedCanvasUpdateRequest & { id: CanvasRetrieve['id']; fields?: string },
  ) => Promise<CanvasRetrieve>
  creator: (params: Omit<CanvasCreateRequest, 'instance_id'>) => Promise<CanvasRetrieve>
  fetcher: (params: GetCanvasesParams & { fields?: string }) => Promise<PaginatedData<CanvasRetrieve>>
  deleter: (id: CanvasRetrieve['id']) => Promise<CanvasRetrieve>
  byIdFetcher: (params: { id: CanvasRetrieve['id']; fields?: string }) => Promise<CanvasRetrieve>
  client: (typeof CLIENT_TYPE)[keyof typeof CLIENT_TYPE]
}>()

const emit = defineEmits<{
  open: [canvas: CanvasRetrieve, newTab?: boolean]
  details: [canvas: CanvasRetrieve]
  edit: [canvas: CanvasRetrieve]
  create: []
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => ({
    accessToken: '',
    instanceId: '',
    apiOrigin: '',
    isAnyTypeOfAdmin: false,
  })),
)

const { t } = useI18n()

const themeVars = useThemeVars()
const confirmation = useConfirmation()
const { toast } = useToast()
const { fetchSuggestedInstanceTags } = useSuggestedTags()

const initialSorting = computed(() => {
  const orderingColumnId = DEFAULT_ORDERING.replace('-', '')
  const hasOrdering = tableColumnsSettings.value.some((item) => item.id === orderingColumnId && item.isSelected)

  if (!hasOrdering) {
    return undefined
  }

  const desc = DEFAULT_ORDERING.startsWith('-')

  return [{ id: orderingColumnId, desc }]
})

const tableRef = ref<InstanceType<typeof CTable> | null>(null)
const toolbarWrapper = ref<HTMLElement | null>(null)
const isCopyingId = ref<CanvasRetrieve['id']>('')
const deletingInProgress = ref<Set<CanvasRetrieve['id']>>(new Set())
const editingColumn = ref<{ id: CanvasRetrieve['id']; column: string } | null>(null)
const fetchedData = ref<CanvasRetrieve[]>([])
provide('availableBlocks', fetchedData)
const isLoading = ref(false)
const isImpact = computed(() => props.client === CLIENT_TYPE.impact)

const { filterComponents: dataFilterComponents } = useCanvasStandardFilters({
  includeTemplateFilter: false,
  includeOwnerFilter: true,
  includeDraftFilter: pitcherInfo.value?.isAnyTypeOfAdmin ?? false,
  ownerLabel: t('canvasUI.composables.useCanvasStandardFilters.createdBy'),
})
const DEFAULT_ORDERING = '-modified_at'

const { height: blockManagementToolbarHeight } = useElementSize(toolbarWrapper)

provide('availableBlocks', fetchedData)

const fetchFields = [
  'access_type',
  'id',
  'name',
  'tags',
  'is_draft',
  'modified_at',
  'modified_by',
  'created_by',
  'is_section',
  'is_block',
  'is_template',
  'thumbnail_url',
  'metadata',
]

const initialFilters: Record<string, any> = {}

const {
  search,
  page,
  pageSize,
  selectedFilters: filters,
  ordering,
} = usePagination({
  storageKey: props.storageKey,
  initialFilters,
  initialOrdering: DEFAULT_ORDERING,
})

const fetchingParams = computed(() => ({
  page_size: pageSize.value,
  page: page.value,
  ordering: ordering.value,
  search: search.value,
}))

const searchFilteredData = computed(() => {
  if (!search.value) return fetchedData.value

  const currSearch = (search.value ?? '').toLocaleLowerCase()

  return fetchedData.value.filter(
    (block) =>
      (block.name.toLocaleLowerCase().includes(currSearch) ||
        block?.tags?.some((tag) => tag.toLocaleLowerCase().includes(currSearch))) ??
      false,
  )
})

const isLoadingTable = computed(() => isLoading.value)

const filteredData = computed(() => {
  const hasActiveFilters =
    filters.value['tags__in'] || filters.value['is_draft'] !== undefined || filters.value['owned_by__name__icontains']

  if (!hasActiveFilters) {
    return searchFilteredData.value
  }

  return searchFilteredData.value.filter((canvas) => {
    // Tags filter
    const matchesTag = filters.value['tags__in']
      ? canvas.tags?.some((tag) => tag.toLowerCase().includes(filters.value['tags__in'].toLowerCase()))
      : true

    // Draft filter
    const matchesDraft =
      filters.value['is_draft'] !== undefined && filters.value['is_draft'] !== null
        ? canvas.is_draft === filters.value['is_draft']
        : true

    // Owner filter
    const matchesOwner = filters.value['owned_by__name__icontains']
      ? (canvas.created_by.name || canvas.created_by.email || '')
          .toLowerCase()
          .includes(filters.value['owned_by__name__icontains'].toLowerCase())
      : true

    return matchesTag && matchesDraft && matchesOwner
  })
})

const columnHelper = createColumnHelper<CanvasRetrieve>()

const defaultColumns = [
  columnHelper.accessor((row) => row.name, {
    id: 'name',
    cell: (cellCtx) =>
      h(
        CTableInput,
        {
          modelValue: cellCtx.getValue(),
          state: isEditingCurrentColumn(cellCtx.row.original.id, cellCtx.column.id) ? 'edit' : 'view',
          readonly: isReadonly(cellCtx.row.original),
          class: 'cursor-pointer',
          valueClass: 'font-700 underline unerline-solid',
          'onUpdate:modelValue': (newValue) => {
            updateProperty(cellCtx.row.original, 'name', newValue)
            clearEditingColumn()
          },
          onClick: (e: MouseEvent) => {
            emit('open', cellCtx.row.original, isModifierClick(e))
          },
          onEdit: () => {
            editingColumn.value = {
              id: cellCtx.row.original.id,
              column: cellCtx.column.id,
            }
          },
          onCancel: clearEditingColumn,
        },
        {
          prefix: () => {
            const thumbnailUrl = (cellCtx.row.original as any).thumbnail_url ?? cellCtx.row.original.metadata?.thumbnail

            return (
              <CThumbnail
                class="mr-2"
                thumbnailUrl={thumbnailUrl}
                item={cellCtx.row.original}
                themeVars={themeVars.value}
                enableThumbnails={pitcherSettings.value.enable_thumbnails_for_templates_and_blocks}
              />
            )
          },
        },
      ),
    header: t('canvasUI.components.blocks.CBlockManagement.nameTh'),
    sortingFn: (a, b) => a.original.name.toLocaleLowerCase().localeCompare(b.original.name.toLocaleLowerCase()),
  }),
  columnHelper.accessor((row) => row.tags, {
    id: 'tags',
    cell: (cellCtx) =>
      h(CTableTag, {
        modelValue: cellCtx.getValue() ?? [],
        state: isEditingCurrentColumn(cellCtx.row.original.id, cellCtx.column.id) ? 'edit' : 'view',
        readonly: isReadonly(cellCtx.row.original),
        rowIndex: cellCtx.row.index,
        suggestedTagsFetcher: (search) =>
          fetchSuggestedInstanceTags(search, { type: ['canvases'], canvasType: 'block' }),
        'onUpdate:modelValue': (newValue) => {
          updateProperty(cellCtx.row.original, 'tags', newValue)
          clearEditingColumn()
        },
        onEdit: () => {
          editingColumn.value = {
            id: cellCtx.row.original.id,
            column: cellCtx.column.id,
          }
        },
        onCancel: clearEditingColumn,
      }),
    header: t('canvasUI.components.blocks.CBlockManagement.tagsTh'),
    enableSorting: false,
    meta: {
      classes: launchDarkly.value.enable_short_tag_column_in_tables ? '!max-w-[200px]' : undefined,
    },
  }),
  columnHelper.accessor((row) => row.is_draft, {
    id: 'status',
    cell: (cellCtx) => (
      <div class="flex items-center">
        <CSwitch
          checked={!cellCtx.getValue()}
          disabled={isReadonly(cellCtx.row.original)}
          onChange={async (newValue: boolean) => await updateProperty(cellCtx.row.original, 'is_draft', !newValue)}
          themeOverrides={{
            railColorActive: themeVars.value.success,
            railColor: themeVars.value.primary6,
          }}
        />
      </div>
    ),
    header: t('canvasUI.components.savedCanvases.CSavedCanvasesManagementStatusCell.status.ready'),
    meta: {
      classes: 'w-[110px]',
    },
  }),
  columnHelper.accessor((row) => row.created_by, {
    id: 'created_by',
    cell: (cellCtx) => {
      const createdBy = cellCtx.row.original.created_by.name || cellCtx.row.original.created_by.email || ''
      return h('div', { class: 'min-w-[150px]' }, createdBy)
    },
    header: t('canvasUI.components.blocks.CBlockManagement.createdByTh'),
    sortingFn: (a, b) => {
      const createdByA = a.original.created_by.name || a.original.created_by.email || ''
      const createdByB = b.original.created_by.name || b.original.created_by.email || ''

      return createdByA.toLocaleLowerCase().localeCompare(createdByB.toLocaleLowerCase())
    },
  }),
  columnHelper.accessor((row) => row.modified_at, {
    id: 'modified_at',
    cell: (cellCtx) => {
      const modifiedBy = cellCtx.row.original.modified_by.name || cellCtx.row.original.modified_by.email
      const modifiedAt = formatDateTime(cellCtx.row.original.modified_at) as string
      const textColor = modifiedBy ? themeVars.value.text : themeVars.value.text3

      return h('div', { class: 'min-w-[250px]' }, [
        h('span', { style: { color: textColor } }, modifiedBy),
        ' ',
        h('span', {}, t('canvasUI.components.blocks.CBlockManagement.atTh')),
        ' ',
        h('span', {}, modifiedAt),
      ])
    },
    header: t('canvasUI.components.blocks.CBlockManagement.modifiedByTh'),
    sortingFn: (a, b) => {
      const dateComparison = dayjs(a.original.modified_at).diff(dayjs(b.original.modified_at))

      if (dateComparison !== 0) return dateComparison

      const modifiedByA = (a.original.modified_by.name || a.original.modified_by.email || '').toLocaleLowerCase()
      const modifiedByB = (b.original.modified_by.name || b.original.modified_by.email || '').toLocaleLowerCase()

      return modifiedByA.localeCompare(modifiedByB)
    },
  }),
  columnHelper.accessor((row) => row, {
    id: 'actions',
    cell: (cellCtx) =>
      h(CBlockManagementActionsCell, {
        hideCopy: isImpact.value,
        hideViewDetails: isImpact.value,
        hideEdit: isReadonly(cellCtx.row.original) || isImpact.value,
        hideDelete: isReadonly(cellCtx.row.original) || isImpact.value,
        hideViewNewTab: isPitcherWkWebView,
        isCopying: isCopyingId.value === cellCtx.row.original.id,
        isDeleting: deletingInProgress.value.has(cellCtx.cell.row.original.id),
        onCopy: async () => {
          try {
            isCopyingId.value = cellCtx.row.original.id
            let copyIdx = 1
            let copyName = `${cellCtx.row.original.name} - ${t('canvasUI.components.blocks.CBlockManagement.copy')}_${copyIdx}`
            while (fetchedData.value.some((canvas) => canvas.name === copyName)) {
              copyIdx++
              copyName = `${cellCtx.row.original.name} - ${t('canvasUI.components.blocks.CBlockManagement.copy')}_${copyIdx}`
            }
            const { is_default, id, owned_by, created_by, ...toCopyFrom } = await props.byIdFetcher({
              id: cellCtx.row.original.id,
            })
            const copy = await props.creator({
              ...toCopyFrom,
              name: copyName,
              tags: toCopyFrom.tags ?? [],
              is_block: true,
              is_section: false,
              is_template: false,
            })
            fetchedData.value = [copy, ...fetchedData.value]

            toast.success(
              t('canvasUI.components.blocks.CBlockManagement.notifications.duplicate.success', {
                name: cellCtx.cell.row.original.name,
              }),
            )
          } catch (e) {
            console.error('block copy failed:', e)

            toast.error(
              t('canvasUI.components.blocks.CBlockManagement.notifications.duplicate.error', {
                name: cellCtx.cell.row.original.name,
              }),
            )
          } finally {
            isCopyingId.value = ''
          }
        },
        onEdit: () => emit('edit', cellCtx.row.original),
        onDelete: () => deleteCanvas(cellCtx.row.original),
        onView: () => emit('open', cellCtx.row.original),
        onViewNewTab: () => emit('open', cellCtx.cell.row.original, true),
        onViewDetails: () => emit('details', cellCtx.row.original),
      }),
    header: t('canvasUI.components.blocks.CBlockManagement.actionsTh'),
    enableSorting: false,
  }),
] as ColumnDef<Record<string, any>, any>[]
const TABLE_COLUMNS_SETTINGS_KEY = 'ciq_blocks_management_table_columns_settings'

const { tableColumnsSettings, columnsFromSettings, onColumnsMove, onColumnsSelect, restoreColumns } =
  useTableColumnsSettings(TABLE_COLUMNS_SETTINGS_KEY, defaultColumns)

async function updateProperty<T extends keyof CanvasRetrieve>(
  item: CanvasRetrieve,
  property: T,
  newValue: CanvasRetrieve[T],
) {
  try {
    const updatedItem = await props.updater({
      id: item.id,
      [property]: newValue,
      fields: fetchFields.join(','),
    })

    const foundIndex = fetchedData.value.findIndex((block) => block.id === item.id)
    if (foundIndex !== -1) {
      fetchedData.value = [
        ...fetchedData.value.slice(0, foundIndex),
        updatedItem,
        ...fetchedData.value.slice(foundIndex + 1),
      ]
    }
  } catch (e) {
    console.error('block update failed:', e)
  }
}

async function deleteCanvas(item: CanvasRetrieve) {
  confirmation.danger({
    hasExit: false,
    title: t('canvasUI.components.blocks.CBlockManagement.deleteDialog.title'),
    content: () =>
      h(CCanvasDeleteDialogContent, {
        name: item?.name,
        texts: {
          name: t('canvasUI.components.blocks.CBlockManagement.deleteDialog.content.name'),
          info: t('canvasUI.components.blocks.CBlockManagement.deleteDialog.content.info'),
        },
      }),
    confirmText: t('canvasUI.components.blocks.CBlockManagement.deleteDialog.confirm'),
    cancelText: t('canvasUI.components.blocks.CBlockManagement.deleteDialog.cancel'),
    onConfirm: async () => {
      try {
        deletingInProgress.value.add(item.id)
        await props.deleter(item.id)
        fetchedData.value = fetchedData.value.filter((canvas) => canvas.id !== item.id)

        toast.success(
          t('canvasUI.components.blocks.CBlockManagement.notifications.delete.success', {
            name: item.name,
          }),
        )
      } catch (e) {
        console.error('block deletion failed:', e)

        toast.error(
          t('canvasUI.components.blocks.CBlockManagement.notifications.delete.error', {
            name: item.name,
          }),
        )
      } finally {
        deletingInProgress.value.delete(item.id)
      }
    },
  })
}

function isReadonly(item: CanvasRetrieve) {
  return (isImpact.value && item.created_by.id !== props.myUserId) || !!editingColumn.value?.id
}

function isEditingCurrentColumn(id: CanvasRetrieve['id'], column: string) {
  return editingColumn.value?.id === id && editingColumn.value?.column === column
}

function clearEditingColumn() {
  editingColumn.value = null
}

function onTableChange(
  event: 'pageChanged' | 'pageSizeChanged' | 'sortChanged' | 'searchChanged' | 'itemsChanged' | 'filterValuesChanged',
  state?: number | SortingState | PaginationState | string | Record<string, any>,
) {
  const initialParams = { ...fetchingParams.value }
  let change
  switch (event) {
    case 'pageChanged':
      change = state as PaginationState
      page.value = change.pageIndex + 1
      break
    case 'pageSizeChanged':
      change = state as number
      pageSize.value = change ?? fetchingParams.value.page_size
      page.value = 1
      break
    case 'sortChanged':
      ;[change] = state as SortingState
      ordering.value = change?.desc ? '-' + change.id : (change?.id ?? DEFAULT_ORDERING)
      page.value = 1
      break
    case 'searchChanged':
      change = state as string
      search.value = change
      page.value = 1
      break
    case 'filterValuesChanged':
      change = state as Record<string, any>
      filters.value = change
      page.value = 1
      break
    case 'itemsChanged':
      page.value = 1
      break
  }

  clearEditingColumn()

  if (event === 'itemsChanged' || !isEqual(initialParams, fetchingParams.value)) {
    refetch(fetchingParams.value)
  }
}

async function refetch(params: { page_size: number; page: number; ordering: string; search: string }) {
  const hasOrdering = tableColumnsSettings.value.some(
    (item) => item.id === DEFAULT_ORDERING.replace('-', '') || item.id === DEFAULT_ORDERING,
  )

  const data = await fetchAll(props.fetcher, {
    ...(params?.search ? params : omit(params, 'search')),
    is_block: true,
    is_section: false,
    is_template: false,
    fields: fetchFields.join(','),
    ordering: hasOrdering ? DEFAULT_ORDERING : undefined,
  })

  fetchedData.value = isImpact.value
    ? data.filter((c) => !c.is_draft || (c.is_draft && c.created_by.id === props.myUserId))
    : data
}

onMounted(async () => {
  try {
    isLoading.value = true
    await refetch(fetchingParams.value)
    isLoading.value = false
  } catch (e) {
    console.error(e)
    isLoading.value = false
  }
})

defineExpose({
  refetch,
})
</script>
